import type { Candle, CandleInterval, TraderPro17StrategyOverlaySettings } from './types';
import type {
  StrategyActiveTrade,
  StrategyMarker,
  StrategyStats,
  StrategyTrade,
} from './overlays/traderPro17Strategy';

export type StrategyExportTrade = StrategyTrade;

export interface StrategyExportMarker extends StrategyMarker {
  time: string | null;
}

export interface TraderPro17StrategyExportBundle {
  schemaVersion: 4;
  meta: {
    strategy: 'Trader Pro 1.7 Strategy';
    symbol: string;
    interval: CandleInterval;
    replayMode: boolean;
    candleCount: number;
    minuteCandleCount: number;
    fees: {
      makerFeePercent: number;
      takerFeePercent: number;
      perTradeRoundTripPercent: number;
      model: 'entry-limit-maker-exit-stop-market-taker';
    };
    exportedAt: string;
  };
  settings: TraderPro17StrategyOverlaySettings;
  stats: StrategyStats;
  activeTrade: StrategyActiveTrade | null;
  markers: StrategyExportMarker[];
  trades: StrategyExportTrade[];
  candles: Candle[];
  minuteCandles: Candle[];
}

interface BuildStrategyExportBundleOptions {
  symbol: string;
  interval: CandleInterval;
  replayMode: boolean;
  exportedAt: Date;
  candles: Candle[];
  minuteCandles: Candle[];
  settings: TraderPro17StrategyOverlaySettings;
  stats: StrategyStats;
  activeTrade: StrategyActiveTrade | null;
  markers: StrategyMarker[];
  trades: StrategyTrade[];
}

function candleTimeAt(candles: Candle[], index: number | null): string | null {
  if (index === null || !Number.isInteger(index) || index < 0 || index >= candles.length) {
    return null;
  }
  return candles[index].time;
}

function sanitizeFilenamePart(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

function formatUtcTimestampForFilename(value: Date): string {
  const year = value.getUTCFullYear();
  const month = String(value.getUTCMonth() + 1).padStart(2, '0');
  const day = String(value.getUTCDate()).padStart(2, '0');
  const hour = String(value.getUTCHours()).padStart(2, '0');
  const minute = String(value.getUTCMinutes()).padStart(2, '0');
  const second = String(value.getUTCSeconds()).padStart(2, '0');
  return `${year}${month}${day}-${hour}${minute}${second}Z`;
}

export function createTraderPro17StrategyExportFilename(
  symbol: string,
  interval: CandleInterval,
  makerFeePercent: number,
  takerFeePercent: number,
  exportedAt: Date,
): string {
  const safeSymbol = sanitizeFilenamePart(symbol);
  const safeInterval = sanitizeFilenamePart(interval);
  const makerFeeToken = Number.isFinite(makerFeePercent)
    ? makerFeePercent.toFixed(4).replace(/\.?0+$/, '').replace('.', 'p')
    : '0';
  const takerFeeToken = Number.isFinite(takerFeePercent)
    ? takerFeePercent.toFixed(4).replace(/\.?0+$/, '').replace('.', 'p')
    : '0';
  const stamp = formatUtcTimestampForFilename(exportedAt);
  return `traderpro17-strategy-${safeSymbol}-${safeInterval}-mf${makerFeeToken}-tf${takerFeeToken}-${stamp}.json`;
}

export function buildTraderPro17StrategyExportBundle(
  options: BuildStrategyExportBundleOptions,
): TraderPro17StrategyExportBundle {
  const { candles, minuteCandles, markers, trades, exportedAt } = options;

  return {
    schemaVersion: 4,
    meta: {
      strategy: 'Trader Pro 1.7 Strategy',
      symbol: options.symbol,
      interval: options.interval,
      replayMode: options.replayMode,
      candleCount: candles.length,
      minuteCandleCount: minuteCandles.length,
      fees: {
        makerFeePercent: options.settings.inputs.makerFeePercent,
        takerFeePercent: options.settings.inputs.takerFeePercent,
        perTradeRoundTripPercent:
          options.settings.inputs.makerFeePercent + options.settings.inputs.takerFeePercent,
        model: 'entry-limit-maker-exit-stop-market-taker',
      },
      exportedAt: exportedAt.toISOString(),
    },
    settings: options.settings,
    stats: options.stats,
    activeTrade: options.activeTrade,
    markers: markers.map((marker) => ({
      ...marker,
      time: candleTimeAt(candles, marker.index),
    })),
    trades: trades.map((trade) => ({
      ...trade,
      entryTime: trade.entryTime ?? candleTimeAt(candles, trade.entryIndex),
      exitTime: trade.exitTime ?? candleTimeAt(candles, trade.exitIndex),
      durationCandles:
        trade.durationCandles ?? (trade.exitIndex === null ? null : trade.exitIndex - trade.entryIndex),
    })),
    candles,
    minuteCandles,
  };
}
